/**
 * Expiring Batches Widget Component
 *
 * Displays a dashboard widget showing summary of batches expiring soon
 * with color-coded urgency metrics.
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-006
 */

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, AlertTriangle, Clock, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ExpiringBatchSummary {
  totalExpiringCount: number;
  criticalCount: number;     // < 7 days (RED)
  warningCount: number;      // 7-14 days (YELLOW)
  normalCount: number;       // 15-30 days (GREEN)
  expiredCount: number;      // Already expired (GRAY)
  mostUrgentBatch?: {
    batchNumber: string;
    daysRemaining: number;
    expiryDate: string;
  };
  actionRequired: boolean;
}

interface ExpiringBatchesWidgetProps {
  summary?: ExpiringBatchSummary;
  loading?: boolean;
  onViewDetails?: () => void;
}

export const ExpiringBatchesWidget: React.FC<ExpiringBatchesWidgetProps> = ({
  summary,
  loading = false,
  onViewDetails
}) => {
  const defaultSummary: ExpiringBatchSummary = {
    totalExpiringCount: 0,
    criticalCount: 0,
    warningCount: 0,
    normalCount: 0,
    expiredCount: 0,
    actionRequired: false
  };

  const data = summary || defaultSummary;

  return (
    <Card className={cn(
      "hover:shadow-md transition-shadow",
      data.actionRequired && "border-red-300 bg-red-50"
    )}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2">
              <CardTitle className="flex items-center gap-2">
                {data.actionRequired ? (
                  <AlertCircle className="w-5 h-5 text-red-600" />
                ) : (
                  <Clock className="w-5 h-5 text-blue-600" />
                )}
                Expiring Batches Alert
              </CardTitle>
            </div>
            <CardDescription>
              Batches expiring in the next 30 days
            </CardDescription>
          </div>
          {data.totalExpiringCount > 0 && (
            <Badge className="text-lg px-3 py-1 bg-blue-100 text-blue-800">
              {data.totalExpiringCount}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Loading expiring batches...</div>
          </div>
        ) : data.totalExpiringCount === 0 ? (
          <div className="text-center py-8 text-gray-600">
            <CheckIcon className="w-12 h-12 mx-auto mb-2 text-green-600 opacity-50" />
            <p className="font-medium">No batches expiring soon</p>
            <p className="text-sm text-gray-500">All batches are in good condition</p>
          </div>
        ) : (
          <>
            {/* Urgency Summary Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Critical (Red) */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center mb-1">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div className="text-2xl font-bold text-red-600">{data.criticalCount}</div>
                <div className="text-xs text-red-700 font-medium">Critical</div>
                <div className="text-xs text-gray-600">&lt;7 days</div>
              </div>

              {/* Warning (Yellow) */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center mb-1">
                  <AlertTriangle className="w-5 h-5 text-yellow-600" />
                </div>
                <div className="text-2xl font-bold text-yellow-600">{data.warningCount}</div>
                <div className="text-xs text-yellow-700 font-medium">Warning</div>
                <div className="text-xs text-gray-600">7-14 days</div>
              </div>

              {/* Normal (Green) */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center mb-1">
                  <Clock className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-2xl font-bold text-green-600">{data.normalCount}</div>
                <div className="text-xs text-green-700 font-medium">Normal</div>
                <div className="text-xs text-gray-600">15-30 days</div>
              </div>

              {/* Expired (Gray) */}
              <div className="bg-gray-50 border border-gray-300 rounded-lg p-3 text-center">
                <div className="flex items-center justify-center mb-1">
                  <AlertCircle className="w-5 h-5 text-gray-600" />
                </div>
                <div className="text-2xl font-bold text-gray-600">{data.expiredCount}</div>
                <div className="text-xs text-gray-700 font-medium">Expired</div>
                <div className="text-xs text-gray-600">Action needed</div>
              </div>
            </div>

            {/* Most Urgent Batch */}
            {data.mostUrgentBatch && (
              <div className="border-t pt-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Most Urgent Batch</p>
                    <p className="text-lg font-semibold text-red-600 mt-1">
                      {data.mostUrgentBatch.batchNumber}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Expires in{' '}
                      <span className="font-semibold text-red-600">
                        {data.mostUrgentBatch.daysRemaining}
                      </span>
                      {' '}days
                    </p>
                  </div>
                  <Badge className="bg-red-100 text-red-800 whitespace-nowrap">
                    {new Date(data.mostUrgentBatch.expiryDate).toLocaleDateString()}
                  </Badge>
                </div>
              </div>
            )}

            {/* Action Button */}
            {onViewDetails && (
              <div className="pt-2 border-t">
                <button
                  onClick={onViewDetails}
                  className={cn(
                    "w-full px-4 py-2 rounded-lg font-medium text-sm transition-colors",
                    data.actionRequired
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : "bg-blue-600 hover:bg-blue-700 text-white"
                  )}
                >
                  View Expiring Batches
                </button>
              </div>
            )}

            {/* Info Message */}
            {data.actionRequired && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                <p className="font-medium">Action Required!</p>
                <p className="text-xs mt-1">
                  You have {data.criticalCount} critical batches that need immediate attention.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Icon component for "no batches" state
const CheckIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);
